import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { SettingsTab } from '../types';
import { SETTINGS_CANCEL_BUTTON_CLASS, SETTINGS_SAVE_BUTTON_CLASS } from './settings-ui';

const TAB_CLASS =
	'relative h-10 px-0 py-[11px] text-[13px] font-normal leading-[18px] bg-transparent rounded-none border-0 shadow-none text-gray-800 transition-colors';

// Ingestion/Handling motion spec, in CSS: the underline slides with a slight spring overshoot; the old panel
// fades out (0.1s), then the new one fades up 6px (0.28s). Off for reduced motion.
const UNDERLINE_CLASS =
	'pointer-events-none absolute bottom-0 left-0 h-0.5 bg-ink-strong transition-[translate,width] duration-[350ms] ease-[cubic-bezier(0.3,1.25,0.5,1)] motion-reduce:transition-none';
const PANEL_OUT_MS = 100;
const PANEL_IN_CLASS =
	'[&>[data-state=active]]:animate-[settings-panel-in_280ms_cubic-bezier(0.22,1,0.36,1)] motion-reduce:[&>[data-state=active]]:animate-none';

interface SettingsPageProps<T extends string> {
	title: string;
	tabs?: readonly SettingsTab<T>[];
	tab?: T;
	onTabChange?: (next: T) => void;
	/** Search box, buttons… shown at the right of the header. */
	tools?: ReactNode;
	/** Sticks under the tabs, e.g. a table's column header. */
	subHeader?: ReactNode;
	children: ReactNode;
}

/** Sticky glass header (title + tabs) over a scrolling body (Figma "Page Header"). */
export function SettingsPage<T extends string>({ title, tabs, tab, onTabChange, tools, subHeader, children }: SettingsPageProps<T>) {
	// The tab whose content is on screen; it trails `tab` while the old panel fades out.
	const [shownTab, setShownTab] = useState(tab);
	const [leaving, setLeaving] = useState(false);
	const [switched, setSwitched] = useState(false);
	useEffect(() => {
		if (tab === shownTab) return;
		// With reduced motion the new panel shows at once, with no fade.
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return setShownTab(tab);
		setLeaving(true);
		const timer = setTimeout(() => {
			setShownTab(tab);
			setLeaving(false);
			setSwitched(true);
		}, PANEL_OUT_MS);
		return () => clearTimeout(timer);
	}, [tab, shownTab]);

	// One underline, moved under the selected tab; no transition until it has been placed once.
	const tabsRef = useRef<HTMLDivElement>(null);
	const [underline, setUnderline] = useState<{ left: number; width: number; placed: boolean } | null>(null);
	useLayoutEffect(() => {
		const row = tabsRef.current;
		if (!row) return;
		const place = () => {
			const active = row.querySelector<HTMLElement>(`[data-tab="${tab}"]`);
			if (active) setUnderline((prev) => ({ left: active.offsetLeft, width: active.offsetWidth, placed: prev !== null }));
		};
		place();
		// Labels change width once the web font loads.
		const observer = new ResizeObserver(place);
		observer.observe(row);
		return () => observer.disconnect();
	}, [tab]);

	const header = (
		<div className='sticky top-0 z-10 shrink-0 bg-white'>
			<div className={cn('flex items-center justify-between gap-3 border-b border-gray-500 px-4', tabs ? 'pt-4' : 'h-[60px]')}>
				<div className='flex flex-col gap-2'>
					<p className='text-[16px] font-semibold leading-6 text-ink'>{title}</p>
					{tabs && (
						<div ref={tabsRef} className='relative'>
							<TabsList className='flex h-auto justify-start gap-4 rounded-none border-0 bg-transparent p-0'>
								{tabs.map((t) => (
									<TabsTrigger
										key={t.value}
										value={t.value}
										data-tab={t.value}
										className={cn(TAB_CLASS, t.value === tab && 'font-medium text-ink-strong')}
									>
										{t.label}
									</TabsTrigger>
								))}
							</TabsList>
							{underline && (
								<span
									aria-hidden
									className={cn(UNDERLINE_CLASS, !underline.placed && 'transition-none')}
									style={{ translate: `${underline.left}px 0`, width: underline.width }}
								/>
							)}
						</div>
					)}
				</div>
				{tools && <div className='flex items-center gap-3'>{tools}</div>}
			</div>
			{subHeader}
		</div>
	);

	return (
		<>
			{tabs && tab && onTabChange ? (
				<Tabs value={shownTab} onValueChange={(value) => onTabChange(value as T)} className='flex min-h-full w-full flex-col gap-0'>
					{header}
					<div
						className={cn(
							'flex flex-1 flex-col',
							leaving && 'opacity-0 transition-opacity duration-100 motion-reduce:transition-none',
							switched && PANEL_IN_CLASS
						)}
					>
						{children}
					</div>
				</Tabs>
			) : (
				<div className='flex min-h-full w-full flex-col'>
					{header}
					{children}
				</div>
			)}
		</>
	);
}

/** The page body: 16px padding, 32px between sections. */
export function SettingsBody({ children, className }: { children: ReactNode; className?: string }) {
	return <div className={cn('flex flex-1 flex-col gap-8 bg-white p-4', className)}>{children}</div>;
}

interface SaveBarProps {
	visible: boolean;
	saving: boolean;
	canSave: boolean;
	onCancel: () => void;
	onSave: () => void;
}

/** Sticky Cancel / Save Changes footer, shown while there are unsaved edits. */
export function SaveBar({ visible, saving, canSave, onCancel, onSave }: SaveBarProps) {
	if (!visible) return null;
	return (
		<div className='sticky bottom-0 z-10 flex shrink-0 items-center justify-end gap-2 border-t border-gray-300 bg-white px-4 py-3'>
			<button
				type='button'
				onClick={onCancel}
				disabled={saving}
				className={SETTINGS_CANCEL_BUTTON_CLASS}
			>
				Cancel
			</button>
			<button
				type='button'
				onClick={onSave}
				disabled={!canSave || saving}
				className={SETTINGS_SAVE_BUTTON_CLASS}
			>
				{saving ? (
					<span className='flex items-center gap-2'>
						<span className='h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent' />
						Saving…
					</span>
				) : (
					'Save Changes'
				)}
			</button>
		</div>
	);
}
